import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

export type ManifestActiveSurface =
  | 'hub'
  | 'lab'
  | 'studio'
  | 'blog'
  | 'personal'
  | 'after-hours'
  | 'connect'

export interface ManifestEntry {
  id: ManifestActiveSurface
  label: string
  href: string
  description?: string
}

export const MANIFEST_ENTRIES: ManifestEntry[] = [
  { id: 'hub', label: 'Hub', href: 'https://labs.tinkernlabs.com', description: 'Studio command center' },
  { id: 'lab', label: 'Lab', href: 'https://labs.tinkernlabs.com/lab', description: 'Products index' },
  { id: 'studio', label: 'Studio', href: 'https://labs.tinkernlabs.com/studio', description: 'Services index' },
  { id: 'blog', label: 'Blog', href: 'https://blog.tinkernlabs.com', description: 'Writing system' },
  { id: 'personal', label: 'Personal', href: 'https://keerthan.tinkernlabs.com', description: 'Engineer identity' },
  { id: 'after-hours', label: 'After Hours', href: 'https://life.tinkernlabs.com', description: 'Human inputs' },
  { id: 'connect', label: 'Connect', href: 'https://labs.tinkernlabs.com/connect', description: 'Contact + testimonials' },
]

interface ManifestProps {
  active?: ManifestActiveSurface
  triggerClassName?: string
  panelClassName?: string
  itemClassName?: string
  activeItemClassName?: string
  labelClassName?: string
  descriptionClassName?: string
}

export default function Manifest({
  active,
  triggerClassName = 'inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-gray-200 hover:text-white border border-white/15 hover:border-white/30 transition-colors',
  panelClassName = 'absolute right-0 mt-2 w-72 rounded-xl border border-white/15 bg-black/85 backdrop-blur-md shadow-2xl p-2 z-50',
  itemClassName = 'block px-3 py-2 rounded-lg hover:bg-white/10 transition-colors',
  activeItemClassName = 'bg-white/10',
  labelClassName = 'text-sm font-medium text-white',
  descriptionClassName = 'text-xs text-gray-400 mt-0.5',
}: ManifestProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Manifest
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={panelClassName} role="menu">
          {MANIFEST_ENTRIES.map((entry) => {
            const isActive = entry.id === active
            return (
              <a
                key={entry.id}
                href={entry.href}
                className={`${itemClassName} ${isActive ? activeItemClassName : ''}`}
                role="menuitem"
                target={isActive ? undefined : '_blank'}
                rel={isActive ? undefined : 'noopener noreferrer'}
              >
                <div className={labelClassName}>{entry.label}</div>
                {entry.description && (
                  <div className={descriptionClassName}>{entry.description}</div>
                )}
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
